import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import {
  LayoutDashboard,
  Folder,
  Upload,
  LogOut,
  Menu,
  X,
  Radio,
  Shield,
  Plus,
} from 'lucide-react';
import { useState } from 'react';

const navigation = [
  { name: 'Operations', href: '/', icon: LayoutDashboard },
  { name: 'Case Feed', href: '/cases', icon: Folder },
  { name: 'Upload Media', href: '/upload', icon: Upload },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCreateCase, setShowCreateCase] = useState(false);

  // Allow create case for Detective James Chen (PIN 602820) identified by name/role
  const canCreateCase = user?.name === 'Detective James Chen' || user?.role === 'LawEnforcement';

  const handleCaseCreated = () => {
    setShowCreateCase(false);
    queryClient.invalidateQueries({ queryKey: ['cases'] });
  };

  return (
    <div className="min-h-screen bg-rtcc-dark">
      {/* Create Case Modal */}
      {showCreateCase && (
        <CreateCaseModal onClose={() => setShowCreateCase(false)} onCreated={handleCaseCreated} />
      )}

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-60 bg-rtcc-panel border-r border-slate-700 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rtcc-accent/20 rounded-lg">
                <Radio className="h-5 w-5 text-rtcc-accent" />
              </div>
              <div>
                <span className="text-white font-bold text-lg tracking-wider">RTCC</span>
                <p className="text-[10px] text-slate-400 -mt-0.5 uppercase tracking-widest">Real-Time Crime Center</p>
              </div>
            </div>
            <button
              className="lg:hidden text-slate-400"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Create Case Button */}
          {canCreateCase && (
            <div className="px-3 pt-4">
              <button
                onClick={() => setShowCreateCase(true)}
                className="flex items-center gap-2 w-full px-3 py-2.5 bg-rtcc-accent text-white text-sm font-medium rounded-lg hover:bg-rtcc-accent/90 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Create Case
              </button>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-rtcc-accent/20 text-rtcc-accent'
                      : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className={`h-5 w-5 ${isActive ? 'text-rtcc-accent' : ''}`} />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User info */}
          {user && (
            <div className="p-4 border-t border-slate-700">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 h-9 w-9 rounded-full bg-slate-600 flex items-center justify-center">
                  <span className="text-white font-bold text-xs">
                    {user.name.split(' ').map((n) => n[0]).join('')}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user.name}</p>
                  <p className="text-xs text-slate-400">{user.role}</p>
                </div>
              </div>
              <button
                onClick={logout}
                className="mt-3 flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-400 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-60">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-rtcc-dark/90 backdrop-blur-sm border-b border-slate-700/50">
          <div className="flex items-center justify-between h-14 px-6">
            <button
              className="lg:hidden text-slate-400"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="hidden lg:flex items-center gap-2 text-xs text-slate-500">
              <Shield className="h-3.5 w-3.5" />
              <span>Powered by DEMS Evidence Platform</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="h-2 w-2 bg-rtcc-accent rounded-full animate-pulse" />
              Live
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// ─── Create Case Modal ────────────────────────────────────────────────────────

function CreateCaseModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const currentYear = new Date().getFullYear();
  const [caseType, setCaseType] = useState<'CR' | 'CV'>('CR');
  const [sequenceNumber, setSequenceNumber] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  // Generate the full case number: YYYY-CR-NNNN or YYYY-CV-NNNN
  const caseNumber = sequenceNumber 
    ? `${currentYear}-${caseType}-${sequenceNumber.padStart(4, '0')}`
    : '';

  const mutation = useMutation({
    mutationFn: () => apiClient.createCase({ 
      title, 
      type: caseType === 'CR' ? 'Criminal' : 'Civil',
      description: description || undefined,
      caseNumber: caseNumber || undefined,
    }),
    onSuccess: onCreated,
    onError: (err: Error) => setError(err.message || 'Failed to create case'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Case title is required');
      return;
    }
    if (!sequenceNumber.trim()) {
      setError('Case sequence number is required');
      return;
    }
    if (!/^\d+$/.test(sequenceNumber)) {
      setError('Sequence must be numeric (e.g., 0042)');
      return;
    }
    setError('');
    mutation.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] px-4">
      <div className="bg-rtcc-panel border border-slate-700 rounded-xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div>
            <h2 className="text-lg font-semibold text-white">Create New Case</h2>
            <p className="text-xs text-slate-400 mt-0.5">DEMS Case Number Format</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Case Number Builder */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">
              Case Number *
            </label>
            <div className="flex items-center gap-2">
              {/* Year (fixed) */}
              <div className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm font-mono">
                {currentYear}
              </div>
              <span className="text-slate-500">-</span>
              {/* Type selector */}
              <div className="flex">
                <button
                  type="button"
                  onClick={() => setCaseType('CR')}
                  className={`px-3 py-2 text-sm font-medium rounded-l-lg border transition-colors ${
                    caseType === 'CR'
                      ? 'bg-rtcc-accent text-white border-rtcc-accent'
                      : 'bg-rtcc-dark text-slate-400 border-slate-600 hover:border-slate-500'
                  }`}
                >
                  CR
                </button>
                <button
                  type="button"
                  onClick={() => setCaseType('CV')}
                  className={`px-3 py-2 text-sm font-medium rounded-r-lg border-t border-b border-r transition-colors ${
                    caseType === 'CV'
                      ? 'bg-rtcc-accent text-white border-rtcc-accent'
                      : 'bg-rtcc-dark text-slate-400 border-slate-600 hover:border-slate-500'
                  }`}
                >
                  CV
                </button>
              </div>
              <span className="text-slate-500">-</span>
              {/* Sequence number */}
              <input
                type="text"
                value={sequenceNumber}
                onChange={(e) => setSequenceNumber(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-24 px-3 py-2 bg-rtcc-dark border border-slate-600 rounded-lg text-white placeholder-slate-500 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-rtcc-accent"
                placeholder="0042"
                maxLength={6}
              />
            </div>
            {caseNumber && (
              <p className="mt-2 text-sm text-rtcc-accent font-mono">
                → {caseNumber}
              </p>
            )}
            <p className="mt-1 text-[10px] text-slate-500">
              CR = Criminal, CV = Civil. Enter your sequence number.
            </p>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">
              Case Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 bg-rtcc-dark border border-slate-600 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-rtcc-accent"
              placeholder="e.g., Armed Robbery Investigation"
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 bg-rtcc-dark border border-slate-600 rounded-lg text-white placeholder-slate-500 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-rtcc-accent"
              placeholder="Brief case description..."
              rows={3}
            />
          </div>

          {error && (
            <p className="text-red-400 text-sm bg-red-900/20 px-3 py-2 rounded-lg">{error}</p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 px-4 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 py-2.5 px-4 bg-rtcc-accent text-white text-sm font-medium rounded-lg hover:bg-rtcc-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {mutation.isPending ? 'Creating...' : 'Create Case'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
